import { useCallback, useEffect, useState } from 'react';
import useUser from './useUser';
import type { UpgradeOfferPlan, UpgradeOfferPresentation } from '../components/UpgradeOfferSummary';

export type UpgradeOffer = UpgradeOfferPresentation & {
  productName: string | null;
  productSlug: string | null;
  checkoutUrl: string | null;
  contactSalesUrl: string | null;
  monthlyAiRetailCreditUsd: string | null;
  resetDate: string;
  plans: UpgradeOfferPlan[];
  terminal: boolean;
};

export default function useUpgradeOffer(enabled = true) {
  const { getApiToken, isAuthenticated, authLoading } = useUser();
  const [offer, setOffer] = useState<UpgradeOffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!enabled || !isAuthenticated || authLoading) return;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
      const response = await fetch(`${apiBaseUrl}/agent/usage-summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.message || 'Could not load plan options.');
      setOffer(payload?.upgrade || null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load plan options.');
    } finally {
      setLoading(false);
    }
  }, [authLoading, enabled, getApiToken, isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { offer, loading, error, refresh };
}
